import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../services/api";
import pdfLayoutService from "../../services/pdfLayout";

const CadastroPDFLayout = () => {
  useTheme();

  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState(null);
  const [footerPreview, setFooterPreview] = useState(null);
  const [tipoLicencaId, setTipoLicencaId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [pdfLayouts, setPdfLayouts] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [tiposLicenca, setTiposLicenca] = useState([]);

  const [fileInputKey, setFileInputKey] = useState(0);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    carregarPdfLayouts();
    carregarTiposLicenca();
  }, []);

  const carregarPdfLayouts = async () => {
    try {
      setLoadingLista(true);
      const response = await pdfLayoutService.listar();
      setPdfLayouts(response.data || []);
    } catch (err) {
      console.error("Erro ao carregar PDF Layouts:", err);
      setError("Erro ao carregar PDF Layouts");
    } finally {
      setLoadingLista(false);
    }
  };

  const carregarTiposLicenca = async () => {
    try {
      const response = await api.get("/TipoLicenca/GetByNomeAsync");
      setTiposLicenca(response.data || []);
    } catch (err) {
      console.error("Erro ao carregar tipos de licença:", err);
    }
  };

  const handleImageChange = (type) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Selecione apenas arquivos de imagem (PNG, JPG, etc.)");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === "header") {
      setHeaderFile(file);
      setHeaderPreview(previewUrl);
    } else {
      setFooterFile(file);
      setFooterPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!headerFile || !footerFile) {
      setError("Selecione as imagens de cabeçalho e rodapé");
      return;
    }

    if (!tipoLicencaId) {
      setError("Selecione um tipo de licença");
      return;
    }

    if (pdfLayouts.some((l) => l.tipoLicencaId === tipoLicencaId)) {
      setError("Já existe um PDF Layout cadastrado para este tipo de licença.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await pdfLayoutService.criar(tipoLicencaId, headerFile, footerFile);

      setSuccess("PDF Layout cadastrado com sucesso!");
      setHeaderFile(null);
      setFooterFile(null);
      setHeaderPreview(null);
      setFooterPreview(null);
      setTipoLicencaId("");
      setFileInputKey((k) => k + 1);

      await carregarPdfLayouts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Erro ao cadastrar PDF Layout:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Erro ao cadastrar PDF Layout",
      );
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogDelete = (layout) => {
    setDeleteTarget(layout);
    setDeleteDialogOpen(true);
  };

  const fecharDialogDelete = () => {
    setDeleteTarget(null);
    setDeleteDialogOpen(false);
  };

  const confirmarDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await pdfLayoutService.deletar(deleteTarget.id);

      setPdfLayouts((prev) =>
        prev.filter((layout) => layout.id !== deleteTarget.id),
      );
      fecharDialogDelete();
      setSuccess("PDF Layout deletado com sucesso!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Erro ao deletar PDF Layout:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Erro ao deletar PDF Layout",
      );
    } finally {
      setDeleting(false);
    }
  };

  const obterNomeTipoLicenca = (tipoLicencaId) => {
    const tipo = tiposLicenca.find((t) => t.id === tipoLicencaId);
    return tipo?.nome || "Não informado";
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ px: { xs: 2, sm: 3, md: 4 } }}
    >
      <Box sx={{ py: { xs: 2, md: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            mb: { xs: 3, md: 4 },
            fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" },
          }}
        >
          Cadastro de PDF Layout
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Novo PDF Layout
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel id="tipo-licenca-label">
                      Tipo de Licença
                    </InputLabel>
                    <Select
                      labelId="tipo-licenca-label"
                      id="tipo-licenca"
                      value={tipoLicencaId}
                      label="Tipo de Licença"
                      onChange={(e) => {
                        setTipoLicencaId(e.target.value);
                        setHeaderFile(null);
                        setFooterFile(null);
                        setHeaderPreview(null);
                        setFooterPreview(null);
                        setFileInputKey((k) => k + 1);
                      }}
                    >
                      {tiposLicenca.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Imagem do Cabeçalho
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    Selecionar Imagem do Cabeçalho
                    <input
                      key={`header-${fileInputKey}`}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageChange("header")}
                    />
                  </Button>
                  {headerPreview && (
                    <Box sx={{ mt: 1, textAlign: "center" }}>
                      <img
                        src={headerPreview}
                        alt="Preview cabeçalho"
                        style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 4 }}
                      />
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Imagem do Rodapé
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    Selecionar Imagem do Rodapé
                    <input
                      key={`footer-${fileInputKey}`}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageChange("footer")}
                    />
                  </Button>
                  {footerPreview && (
                    <Box sx={{ mt: 1, textAlign: "center" }}>
                      <img
                        src={footerPreview}
                        alt="Preview rodapé"
                        style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 4 }}
                      />
                    </Box>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Enviando...
                    </>
                  ) : (
                    "Cadastrar PDF Layout"
                  )}
                </Button>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                minHeight: { xs: 300, md: 500 },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, p: 3, pb: 0 }}>
                PDF Layouts Cadastrados
              </Typography>

              {loadingLista ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: { xs: 200, md: 400 },
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : pdfLayouts.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="textSecondary">
                    Nenhum PDF Layout cadastrado
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Tipo de Licença
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pdfLayouts.map((layout) => (
                        <TableRow key={layout.id}>
                          <TableCell>
                            {obterNomeTipoLicenca(layout.tipoLicencaId)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => abrirDialogDelete(layout)}
                              title="Deletar"
                              aria-label={`Excluir layout de ${obterNomeTipoLicenca(layout.tipoLicencaId)}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          open={deleteDialogOpen}
          onClose={fecharDialogDelete}
          fullWidth
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: { m: { xs: 2, sm: 3 } },
          }}
        >
          <DialogTitle id="alert-dialog-title">Deletar PDF Layout</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Tem certeza que deseja deletar este PDF Layout associado a{" "}
              <strong>
                {deleteTarget &&
                  obterNomeTipoLicenca(deleteTarget.tipoLicencaId)}
              </strong>
              ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharDialogDelete}>Cancelar</Button>
            <Button
              onClick={confirmarDelete}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? "Deletando..." : "Deletar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CadastroPDFLayout;
